<template>
  <div class="response-section">
    <div class="row">
      <div>
        <api-header :id="id" tag="h4" class="flex flex-row items-center m-0">
          {{ $t('documentation.api.responses.title') }}
          <span class="grow" />
          <api-content-type-selector
            v-if="contentsTypes"
            :id="id + 'response-content-type'"
            :types="contentsTypes"
            @selected="idx => (contentIdx = idx)"
          />
        </api-header>
        <div class="flex flex-row button-row">
          <v-button
            v-for="({ status }, idx) in responses"
            :key="status"
            :label="status"
            :appearance="responseIdx === idx ? 'filled' : 'outlined'"
            size="condensed"
            class="button-width"
            :status="status"
            connotation="accent"
            @click="responseIdx = idx"
          />
        </div>
        <html-text class="description" tag="span" :html="response.description" />
        <div v-if="headers" class="my-9">
          <api-header :id="id + '-header'" class="capitalize" tag="h5">
            <i18n-t keypath="documentation.api.parameters.title" tag="span" scope="global">
              <template #type>
                {{ $t(`documentation.api.responses.header`) }}
              </template>
            </i18n-t>
          </api-header>
          <div v-for="header in headers" :key="header.name">
            <api-schema-property :schema="header.content.schema" :parameter="header.name">
              <template v-if="header.content.description" #description>
                <html-text tag="span" :html="header.content.description" />
              </template>
            </api-schema-property>
          </div>
        </div>
      </div>
    </div>
    <api-schema-object
      v-if="content && content.schema"
      :key="`${responseIdx}-${contentIdx}`"
      type="response"
      :schema="content.schema"
      :example-generator-args="content.exampleGeneratorArgs"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import { Responses } from '@bifrost-dev-docs-mfe/common/types/apiDocs';
import { VButton } from '@vonage/vivid-vue';
import ApiHeader from '@/components/api/content/common/ApiHeader.vue';
import HtmlText from '@/components/utils/HtmlText.vue';
import ApiContentTypeSelector from '@/components/api/content/common/ApiContentTypeSelector.vue';
import ApiSchemaObject from '@/components/api/content/operations/schema/ApiSchemaObject.vue';
import ApiSchemaProperty from '@/components/api/content/operations/schema/ApiSchemaProperty.vue';

const props = defineProps({
  id: { type: String, required: true },
  responses: { type: Array as PropType<Responses>, required: true },
});

const responseIdx = ref(0);
const response = computed(() => props.responses[responseIdx.value]);

const contentIdx = ref(0);
const contents = computed(() => response.value && response.value.contents);
const headers = computed(() => response.value && response.value.headers);
const contentsTypes = computed(() => contents.value && contents.value.map(({ type }) => type));
const content = computed(() => contents.value && contents.value[contentIdx.value]);
</script>

<style lang="scss">
.response-section {
  .button-row {
    gap: 8px;
    margin-bottom: 8px;

    .button-width {
      width: 64px;
    }

    dev-docs-button {
      &[status^='2'] {
        --vvd-button-accent-primary: #008000;
        --vvd-button-accent-firm: #008000;
        --vvd-button-accent-faint: rgb(242, 242, 242);
      }

      &[status^='3'] {
        --vvd-button-accent-primary: #ca3500;
        --vvd-button-accent-firm: #ca3500;
        --vvd-button-accent-faint: rgb(242, 242, 242);
      }

      &[status^='4'] {
        --vvd-button-accent-primary: #e60000;
        --vvd-button-accent-firm: #e60000;
        --vvd-button-accent-faint: rgb(242, 242, 242);
      }

      &[status^='5'] {
        --vvd-button-accent-primary: #800000;
        --vvd-button-accent-firm: #800000;
        --vvd-button-accent-faint: rgb(242, 242, 242);
      }
    }
  }
  .description p {
    padding-top: 8px;
    font-size: 16px !important;
    font-weight: 600;
  }
}
</style>
